import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { useQueryClient } from '@tanstack/react-query';

export type CleanReactQueryBehaviour = 'none' | 'onPop' | 'onDispose';

export interface BasePageLifecycle {
  /**
   * Optional screen key to differentiate between multiple instances of the same route
   * This is the navigation screen key from RouteStack (e.g., 'user-details-a3f9k2x7')
   *
   * When provided:
   * - Lifecycle events only fire for the matching screen key instance
   * - Multiple instances of the same pathname can coexist with independent lifecycles
   *
   * When omitted:
   * - Falls back to pathname-only matching (legacy behavior)
   * - All instances with same pathname share lifecycle events
   *
   * Use for:
   * - Video pages that need independent playback control per instance
   * - Detail pages that can be stacked multiple times
   *
   * Example: UserVideosPage A → UserDetailsPage → UserVideosPage B
   * - With screenKey: Only UserVideosPage B receives onPopNext when returning
   * - Without screenKey: Both UserVideosPage A and B receive onPopNext (incorrect!)
   */
  screenKey?: string;

  /**
   * Called once when component mounts (similar to Flutter's initState)
   * Can return a cleanup function that runs on unmount
   *
   * Use for:
   * - Initial data fetching
   * - Setting up listeners
   * - Initializing state
   * - Video player initialization
   *
   * Flutter equivalent: initState()
   */
  onInit?: () => void | (() => void);

  /**
   * Called when this route is first pushed (navigated to)
   * Only fires once on initial navigation, not when returning from other routes
   * 
   * Use for:
   * - Starting initial animations
   * - Analytics (screen view)
   * - Initial video setup (but don't autoplay yet)
   * 
   * Flutter equivalent: didPush()
   * 
   * Note: Called after onInit on first mount
   * 
   * Parameter 'repushed' indicates if this is a re-push after being popped
   */
  onPush?: (repushed: boolean) => void | (() => void);

  /**
   * Called when returning to this route (a route on top was popped)
   * The component was hidden and is now visible again
   * 
   * Use for:
   * - Resuming video playback
   * - Resuming animations
   * - Refreshing stale data
   * - Analytics (screen re-entry)
   * 
   * Flutter equivalent: didPopNext()
   * 
   * This is the key difference from onPush - use this to resume playback!
   */
  onPopNext?: () => void | (() => void);

  /**
   * Called when a new route is pushed on top of this one
   * This route becomes hidden but stays in memory
   * 
   * Use for:
   * - Pausing video playback
   * - Pausing animations
   * - Saving temporary state
   * 
   * Flutter equivalent: didPushNext()
   */
  onPushNext?: () => void;

  /**
   * Called when this route is being popped (navigated away via back)
   * 
   * Use for:
   * - Cleanup before leaving
   * - Analytics (screen exit)
   * 
   * Flutter equivalent: didPop()
   */
  onPop?: () => void;

  /**
   * Called when component unmounts (removed from DOM)
   * Note: With KeepAlive, this only happens when cache is cleared
   * 
   * Use for:
   * - Final cleanup
   * - Cancelling pending requests
   * - Disposing resources
   * 
   * Flutter equivalent: dispose()
   */
  onDispose?: () => void;

  /**
   * Debug mode - logs lifecycle events to console
   * Set to true during development to see lifecycle logs
   * Default: false
   */
  debug?: boolean;

  /**
   * Automatic React Query cache cleanup behavior
   * - 'none': No automatic cleanup
   * - 'onPop': Clean React Query cache when this route is popped (going back from this page) (default)
   * - 'onDispose': Clean React Query cache when component unmounts
   *
   * Uses screenKey as prefix to remove all queries for this component instance
   * Example: Removes all queries where queryKey[0] === screenKey
   *
   * Default: 'onPop'
   */
  cleanReactQueryBehaviour?: CleanReactQueryBehaviour;
}

export interface BasePageState {
  /** Current route pathname */
  pathname: string;
  /** Navigation type (PUSH, REPLACE, POP) */
  navigationType: ReturnType<typeof useNavigationType>;
  /** Whether this is the initial mount */
  isInitialMount: boolean;
}

/**
 * Base page lifecycle hook - provides Flutter-like RouteObserver lifecycle methods
 * 
 * Lifecycle order:
 * 1. onInit (once on mount) - Flutter: initState()
 * 2. onPush (first navigation) - Flutter: didPush()
 * 3. onPushNext (when leaving) - Flutter: didPushNext()
 * 4. onPopNext (when returning) - Flutter: didPopNext()
 * 5. onPop (when being popped) - Flutter: didPop()
 * 6. onDispose (when unmounted) - Flutter: dispose()
 * 
 * @example
 * ```tsx
 * export const VideoPage = () => {
 *   const { currentScreenKeyRef } = useKeepAlive();
 *   const screenKey = useRef(currentScreenKeyRef.current || generateScreenKey('video')).current;
 *
 *   useBasePage({
 *     screenKey, // Pass screen key for instance differentiation
 *     onInit: () => {
 *       console.log('Initialize video player');
 *       return () => console.log('Cleanup');
 *     },
 *     onPush: () => {
 *       console.log('First time on page - setup but don\'t autoplay');
 *     },
 *     onPopNext: () => {
 *       console.log('Returned to page - resume video playback');
 *       videoRef.current?.play();
 *     },
 *     onPushNext: () => {
 *       console.log('Navigating away - pause video');
 *       videoRef.current?.pause();
 *     },
 *     onPop: () => {
 *       console.log('Page being popped - cleanup');
 *     },
 *     onDispose: () => {
 *       console.log('Final cleanup');
 *     },
 *     cleanReactQueryBehaviour: 'onPop', // Auto-cleanup React Query cache on POP
 *     debug: true,
 *   });
 *
 *   return <video ref={videoRef} />;
 * };
 * ```
 */
export const useBasePage = (lifecycle: BasePageLifecycle = {}): BasePageState => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const queryClient = useQueryClient();
  const isInitialMount = useRef(true);
  const previousPath = useRef<string | null>(null);
  const previousScreenKey = useRef<string | undefined>(undefined);
  const hasBeenAttached = useRef(false); // Track if onPush has fired
  const pushCleanupRef = useRef<(() => void) | void>();
  const popNextCleanupRef = useRef<(() => void) | void>();

  // Get current screen key from RouteStack (if available)
  let currentScreenKey: string | undefined;
  try {
    const keepAliveContext = useKeepAlive();
    currentScreenKey = keepAliveContext.currentScreenKeyRef.current;
  } catch {
    // Not within KeepAliveProvider - screenKey matching not available
    currentScreenKey = undefined;
  }

  // Capture the path and screen key this component was mounted with - NEVER changes after first render
  const componentPath = useRef<string | null>(null);
  const componentScreenKey = useRef<string | undefined>(lifecycle.screenKey);
  if (componentPath.current === null) {
    componentPath.current = location.pathname;
    componentScreenKey.current = lifecycle.screenKey;
  }

  // CRITICAL: Track the last navigation transition to deduplicate lifecycle events
  // When multiple components are mounted and React batches updates, the same lifecycle
  // can fire multiple times. We use a combination of path + screenKey + timestamp to detect duplicates.
  const lastTransitionRef = useRef<{ path: string; screenKey: string | undefined; timestamp: number } | null>(null);

  const debug = lifecycle.debug ?? false;
  const cleanReactQueryBehaviour = lifecycle.cleanReactQueryBehaviour ?? 'onPop';
  var pageName = componentPath.current;
  if (pageName === '/') {
    pageName = '/ (Root / Feed)';
  }

  // Helper function to clean React Query cache for this screen key
  const cleanReactQueryCache = () => {
    if (!componentScreenKey.current) {
      if (debug) {
        console.warn(`⚠️ [${pageName}] Cannot clean React Query cache - no screen key available`);
      }
      return;
    }

    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] === componentScreenKey.current
    });

    // if (process.env.NODE_ENV !== 'production') {
    //   console.log(`🗑️ [${pageName}] Cleaned React Query cache for screen key:`, componentScreenKey.current);
    // }
  };

  // Helper to check if current route matches this component's route
  // Takes into account both pathname and optional screenKey
  const checkIfComponentActive = (currentPath: string, currentKey?: string): boolean => {
    const pathMatches = currentPath === componentPath.current;

    // If component has a screenKey, both path and screenKey must match
    if (componentScreenKey.current) {
      return pathMatches && currentKey === componentScreenKey.current;
    }

    // If no screenKey provided, use legacy pathname-only matching
    return pathMatches;
  };

  // onInit - Runs once on component mount
  useEffect(() => {
    if (debug) {
      console.log(`🔷 [${pageName}] onInit - Component mounted`);
    }

    const initCleanup = lifecycle.onInit?.();

    return () => {
      // onDispose - Cleanup when component unmounts (rare with KeepAlive)
      if (debug) {
        console.log(`🔶 [${pageName}] onDispose - Component unmounting`);
      }

      lifecycle.onDispose?.();

      // Clean React Query cache if configured for onDispose
      if (cleanReactQueryBehaviour === 'onDispose') {
        cleanReactQueryCache();
      }

      if (typeof initCleanup === 'function') {
        initCleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation lifecycle - Handle push/pop events (Flutter RouteObserver pattern)
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPathValue = previousPath.current;
    const previousKeyValue = previousScreenKey.current;

    // CRITICAL FIX: Initialize previousPath and previousScreenKey on first effect run
    // This prevents the mismatch where previousPath is set but previousScreenKey is undefined
    // Note: We capture the values BEFORE updating refs, so isPathChange will be true on first run
    const isFirstRun = previousPathValue === null;
    if (isFirstRun) {
      previousPath.current = currentPath;
      previousScreenKey.current = currentScreenKey;
      // Don't return early - let isInitialMount handle the initial lifecycle
    }

    const isPathChange = previousPathValue !== currentPath;
    const isKeyChange = previousKeyValue !== currentScreenKey;
    const isRouteChange = isPathChange || isKeyChange;

    // Check if this component instance is currently active (considering both path and screenKey)
    const isThisComponentActive = checkIfComponentActive(currentPath, currentScreenKey);
    // On first run, previousPathValue is null, so wasThisComponentActive should be false
    const wasThisComponentActive = previousPathValue !== null
      ? checkIfComponentActive(previousPathValue, previousKeyValue)
      : false;

    // Always update previous values to track navigation
    // Skip if this is the first run (already updated in isFirstRun block above)
    if (isRouteChange && !isFirstRun) {
      previousPath.current = currentPath;
      previousScreenKey.current = currentScreenKey;
    }

    if (isInitialMount.current) {
      // Initial mount - only fire if this component instance is currently active
      if (!isThisComponentActive) {
        if (debug) {
          console.log(`⚠️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] Skipping initial mount - not active (current: ${currentPath}${currentScreenKey ? ` / ${currentScreenKey}` : ''})`);
        }
        isInitialMount.current = false;
        return;
      }

      isInitialMount.current = false;
      hasBeenAttached.current = true;

      if (debug) {
        console.log(`✅ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] onPush - Route pushed (initial navigation, navType: ${navigationType})`);
      }

      // Fire onPush (first time route is visible)
      pushCleanupRef.current = lifecycle.onPush?.(false);

      return;
    }

    // Handle route changes after initial mount
    if (isRouteChange) {
      if (wasThisComponentActive && !isThisComponentActive) {
        // Leaving this route
        // DEDUPLICATION: Check if we already fired this lifecycle event
        const now = Date.now();
        const lastTransition = lastTransitionRef.current;

        // Skip if we fired the same transition within the last 50ms (React batch window)
        if (lastTransition &&
          lastTransition.path === currentPath &&
          lastTransition.screenKey === currentScreenKey &&
          now - lastTransition.timestamp < 50) {
          if (debug) {
            console.log(`⏭️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] Skipping duplicate leave event`);
          }
          return;
        }

        lastTransitionRef.current = { path: currentPath, screenKey: currentScreenKey, timestamp: now };

        if (debug) {
          const isPopping = navigationType === 'POP';
          if (isPopping) {
            console.log(`⬅️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] onPop - This route is being popped`);
          } else {
            console.log(`➡️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] onPushNext - New route pushed on top`);
          }
        }

        // Clean up push/popNext callbacks
        if (typeof pushCleanupRef.current === 'function') {
          pushCleanupRef.current();
          pushCleanupRef.current = undefined;
        }
        if (typeof popNextCleanupRef.current === 'function') {
          popNextCleanupRef.current();
          popNextCleanupRef.current = undefined;
        }

        // Fire appropriate leave event
        if (navigationType === 'POP') {
          lifecycle.onPop?.();

          // Clean React Query cache if configured for onPop
          if (cleanReactQueryBehaviour === 'onPop') {
            cleanReactQueryCache();
          }
        } else {
          lifecycle.onPushNext?.();
        }

      } else if (!wasThisComponentActive && isThisComponentActive) {
        // Returning to this route (it was hidden, now visible again)
        // DEDUPLICATION: Check if we already fired this lifecycle event
        const now = Date.now();
        const lastTransition = lastTransitionRef.current;

        // Skip if we fired the same transition within the last 50ms (React batch window)
        if (lastTransition &&
          lastTransition.path === currentPath &&
          lastTransition.screenKey === currentScreenKey &&
          now - lastTransition.timestamp < 50) {
          if (debug) {
            console.log(`⏭️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] Skipping duplicate return event`);
          }
          return;
        }

        lastTransitionRef.current = { path: currentPath, screenKey: currentScreenKey, timestamp: now };

        const isReturningViaPop = navigationType === 'POP';
        const repushed = !isReturningViaPop;

        if (debug) {
          if (isReturningViaPop) {
            console.log(`↩️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] onPopNext - Returning to this route (popped back)`);
          } else {
            console.log(`✅ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] onPush - Route pushed again`);
          }
        }

        if (isReturningViaPop && hasBeenAttached.current) {
          // Fire onPopNext - returning from another route
          popNextCleanupRef.current = lifecycle.onPopNext?.();
        } else {
          // Fire onPush - first time or pushed again
          hasBeenAttached.current = true;
          pushCleanupRef.current = lifecycle.onPush?.(repushed);
        }

      } else if (!isThisComponentActive && !wasThisComponentActive) {
        // Route changed but this component instance not involved - ignore
        if (debug) {
          console.log(`ℹ️ [${pageName}${componentScreenKey.current ? ` (${componentScreenKey.current})` : ''}] Route changed ${previousPathValue}${previousKeyValue ? `/${previousKeyValue}` : ''} → ${currentPath}${currentScreenKey ? `/${currentScreenKey}` : ''} (not involving this instance)`);
        }
        return;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentScreenKey]);

  return {
    pathname: location.pathname,
    navigationType,
    isInitialMount: isInitialMount.current,
  };
};
